import InfiniteScroll from 'react-infinite-scroll-component';
import { Role } from 'auth';
import dayjs, { Dayjs } from 'dayjs';
import { get, merge } from 'lodash';
import isEmpty from 'lodash/isEmpty';
import omit from 'lodash/omit';
import pickBy from 'lodash/pickBy';
import { FC, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { withPrivateAccess } from '@/modules/auth/hocs/withPrivateAccess.tsx';
import ArchiveSwitch from '@/modules/common/components/ArchiveSwitch.tsx';
import { useGetCurrentBusinessId } from '@/modules/common/hooks/useGetCurrentBusinessId.ts';
import { useTasksListQuery } from '@/modules/common/queries/tasks.ts';
import '@/modules/common/styles/common.css';
import TasksFilters from '@/modules/admin-dashboard/components/TasksFilters.tsx';
import TaskHistoryItem from '@/modules/admin-dashboard/components/items/TaskHistoryItem.tsx';
import TaskItem from '@/modules/admin-dashboard/components/items/TaskItem.tsx';
import { CreateTaskModal } from '@/modules/admin-dashboard/components/modals/CreateTaskModal.tsx';
import {
  Button,
  EmptyListState,
  ModalProviderContext,
  PageLayout,
  SearchField,
  SelectPaginateOption,
  Spinner,
} from 'ui';
import {
  GetTaskListV1TasksGetParams,
  RegisterResult,
  TaskListMode,
  TaskListWithRelationsResponse,
} from 'compliance-api';

const mapTabToListItem: Record<
  TaskListMode,
  FC<{
    task: TaskListWithRelationsResponse;
    isUpcoming?: boolean;
    archiveEnabled?: boolean;
    taskIdToOpen?: number | null;
    taskResultIdToOpen?: number | null;
    onOpenFromUrl: () => void;
    filterByName?: string;
  }>
> = {
  [TaskListMode.Todo]: TaskItem,
  [TaskListMode.Deleted]: TaskItem,
  [TaskListMode.Upcoming]: TaskItem,
  [TaskListMode.Overdue]: TaskItem,
  [TaskListMode.History]: TaskHistoryItem,
  [TaskListMode.AllTasks]: TaskItem,
};
export const mapTabToEmptyStateText: Record<TaskListMode, { title: string; content: string }> = {
  [TaskListMode.Todo]: {
    title: 'No tasks are incoming',
    content:
      'A task is a specific action assigned to a user, designed to help your business meet its compliance requirements. Each task comes with a form to fill out and may have a deadline. Completing tasks keeps track with compliance obligations using compliance questions in forms. You can generate reports based on task results.',
  },
  [TaskListMode.Deleted]: {
    title: 'No tasks are incoming',
    content:
      'A task is a specific action assigned to a user, designed to help your business meet its compliance requirements. Each task comes with a form to fill out and may have a deadline. Completing tasks keeps track with compliance obligations using compliance questions in forms. You can generate reports based on task results.',
  },
  [TaskListMode.Upcoming]: {
    title: 'There are no tasks for the future',
    content:
      'A future task is a scheduled action that will become available to a user at a set time to support ongoing compliance. Once active, it includes a form and may have a deadline. Completing it ensures alignment with compliance obligations, and results can be used in reports.',
  },
  [TaskListMode.Overdue]: {
    title: 'No tasks are overdue',
    content: 'This page shows tasks which expired their due date, but still can be completed.',
  },
  [TaskListMode.History]: {
    title: 'No completed tasks yet',
    content:
      'From this page you can see task completions, overdue tasks and view task completion results inputted to a form by the assignees',
  },
  [TaskListMode.AllTasks]: {
    title: 'No tasks available',
    content:
      'This view shows all tasks—whether pending, upcoming, overdue, or completed. Once tasks are created or assigned, they will appear here for tracking, action, or review to support compliance processes.',
  },
};
export interface Filters {
  archive?: boolean;
  name: string;
  assigneeUser?: SelectPaginateOption<number>;
  register?: SelectPaginateOption<number>;
  form?: SelectPaginateOption<number>;
  filter_by_compliance_status?: RegisterResult;
  from_date?: string | Dayjs;
  to_date?: string | Dayjs;
  clientId?: number;
  mode?: TaskListMode;
}
type filtersSet = Record<TaskListMode, Filters>;
const getInitFiltersSet = (): filtersSet => {
  const initFilters = Object.values(TaskListMode).reduce(
    (acc, curr) => ({
      ...acc,
      [curr]: {
        name: '',
        from_date: get(history, 'state.usr.from_date', ''),
        to_date: get(history, 'state.usr.to_date', ''),
      },
    }),
    {} as filtersSet,
  );
  const stateFilters = get(history.state, 'usr.initFilters', {});
  return merge(initFilters, stateFilters);
};
interface TasksProps {
  clientId?: number;
}

interface TasksNavigationState {
  from_date?: string;
  to_date?: string;
}

const Tasks = ({ clientId }: TasksProps) => {
  const businessId = useGetCurrentBusinessId();
  const initialFiltersSet = useMemo(() => {
    const initFilters = getInitFiltersSet();

    if (clientId) {
      initFilters[TaskListMode.AllTasks].mode = TaskListMode.History;
    }

    return initFilters;
  }, [clientId]);
  const [filtersSet, setFiltersSet] = useState<filtersSet>(initialFiltersSet);

  useEffect(() => {
    setFiltersSet(initialFiltersSet);
  }, [businessId, initialFiltersSet]);

  const { openModal } = useContext(ModalProviderContext);
  const location = useLocation();
  const navigate = useNavigate();

  const [searchParams, setSearchParams] = useSearchParams();

  const [taskIdToOpenIdFromUrl, setTaskIdToOpenIdFromUrl] = useState<number | null>(null);
  const [taskResultIdToOpenFromUrl, setTaskResultIdToOpenFromUrl] = useState<number | null>(null);

  const itemRefs = useRef<Record<number, HTMLDivElement | null>>({});

  const { category } = useParams();

  // Determine initial mode from URL category or default
  const initialMode =
    category && Object.values(TaskListMode).includes(category as TaskListMode)
      ? (category as TaskListMode)
      : TaskListMode.Todo;

  // Get filters: use AllTasks mode key for client pages to store client-specific filters
  const modeForFilters = clientId ? TaskListMode.AllTasks : initialMode;
  const filters = filtersSet[modeForFilters];
  const startDateFilterFromQuery = searchParams.get('start_date');
  const endDateFilterFromQuery = searchParams.get('end_date');
  const startDateFilterFromState = (location.state as TasksNavigationState | null)?.from_date;
  const endDateFilterFromState = (location.state as TasksNavigationState | null)?.to_date;

  // Final mode: use filters.mode if clientId exists (default to History), otherwise use initialMode
  const mode = clientId ? filters.mode || TaskListMode.History : initialMode;
  const normalizedFilters: Partial<GetTaskListV1TasksGetParams> = useMemo(
    () =>
      pickBy(
        {
          filter_by_name: filters.name,
          filter_by_assignee_users: filters.assigneeUser?.value,
          filter_by_registers: filters.register?.value,
          filter_by_form_ids: filters.form?.value,
          filter_by_compliance_status: filters.filter_by_compliance_status,
          from_date: filters.from_date ? dayjs(filters.from_date).toISOString() : null,
          to_date: filters.to_date ? dayjs(filters.to_date).endOf('d').toISOString() : null,
        },
        Boolean,
      ),
    [filters],
  );
  const { hasNextPage, fetchNextPage, data, isLoading } = useTasksListQuery({
    page: 1,
    page_size: 1000, // todo: change after fixing pagination on the backend
    mode: filters.archive ? TaskListMode.Deleted : mode,
    ...normalizedFilters,
    filter_by_client_id: clientId,
  });
  const tasks = useMemo(() => {
    return (data?.pages || []).flatMap(({ data }) => data);
  }, [data?.pages]);

  useEffect(() => {
    const nextFromDate =
      startDateFilterFromQuery && dayjs(startDateFilterFromQuery).isValid()
        ? startDateFilterFromQuery
        : null;
    const nextToDate =
      endDateFilterFromQuery && dayjs(endDateFilterFromQuery).isValid()
        ? endDateFilterFromQuery
        : null;

    if (!nextFromDate && !nextToDate) return;

    setFiltersSet(currentFiltersSet => ({
      ...currentFiltersSet,
      [modeForFilters]: {
        ...currentFiltersSet[modeForFilters],
        ...(nextFromDate ? { from_date: nextFromDate } : {}),
        ...(nextToDate ? { to_date: nextToDate } : {}),
      },
    }));

    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('start_date');
    nextParams.delete('end_date');
    setSearchParams(nextParams, { replace: true });
  }, [
    endDateFilterFromQuery,
    modeForFilters,
    searchParams,
    setFiltersSet,
    setSearchParams,
    startDateFilterFromQuery,
  ]);

  useEffect(() => {
    const nextFromDate =
      startDateFilterFromState && dayjs(startDateFilterFromState).isValid()
        ? startDateFilterFromState
        : null;
    const nextToDate =
      endDateFilterFromState && dayjs(endDateFilterFromState).isValid()
        ? endDateFilterFromState
        : null;

    if (!nextFromDate && !nextToDate) return;

    setFiltersSet(currentFiltersSet => ({
      ...currentFiltersSet,
      [modeForFilters]: {
        ...currentFiltersSet[modeForFilters],
        ...(nextFromDate ? { from_date: nextFromDate } : {}),
        ...(nextToDate ? { to_date: nextToDate } : {}),
      },
    }));

    navigate(
      {
        pathname: location.pathname,
        search: location.search,
      },
      {
        replace: true,
        state: null,
      },
    );
  }, [
    endDateFilterFromState,
    location.pathname,
    location.search,
    modeForFilters,
    navigate,
    setFiltersSet,
    startDateFilterFromState,
  ]);

  useEffect(() => {
    const taskResultId = searchParams.get('task_result_id');

    if (taskResultId && tasks.length) {
      const numericTaskResultId = Number(taskResultId);
      const taskToOpen = tasks.find(task =>
        task.task_results?.some(result => result.id === numericTaskResultId),
      );

      if (taskToOpen) {
        setTaskIdToOpenIdFromUrl(taskToOpen.id);
        setTaskResultIdToOpenFromUrl(numericTaskResultId);
        setTimeout(() => {
          itemRefs.current[taskToOpen.id]?.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          });
        }, 100);
      }
    }
  }, [searchParams, tasks]);

  const handleSearchChange = useCallback(
    (name: string) => {
      setFiltersSet(filtersSet => ({
        ...filtersSet,
        [modeForFilters]: { ...filtersSet[modeForFilters], name },
      }));
    },
    [modeForFilters, setFiltersSet],
  );

  const handleChangeFilters = useCallback(
    (cb: (filters: Filters) => Filters) => {
      setFiltersSet(filtersSet => ({
        ...filtersSet,
        [modeForFilters]: cb(filtersSet[modeForFilters]),
      }));
    },
    [modeForFilters, setFiltersSet],
  );

  const handleToggleArchiveFilter = useCallback(
    (active: boolean) => {
      handleChangeFilters(filters =>
        active ? { ...filters, archive: true } : omit(filters, ['archive']),
      );
    },
    [handleChangeFilters],
  );
  const headerAction = (
    <div className="flex max-w-[650px] flex-wrap-reverse w-full mt-5 md:mt-0 justify-end items-center gap-3">
      {mode === TaskListMode.AllTasks && (
        <ArchiveSwitch active={!!filters.archive} onChange={handleToggleArchiveFilter} />
      )}
      <SearchField
        placeholder="Search"
        value={filters.name}
        onChange={handleSearchChange}
        className="w-[240px]"
      />
      <div className="w-fit">
        <Button onClick={() => openModal(CreateTaskModal, { clientId })} variant="primary">
          Add new task
        </Button>
      </div>
    </div>
  );
  const TaskItem = mapTabToListItem[mode];
  return (
    <PageLayout<TaskListMode> title="Tasks" headerAction={headerAction}>
      <TasksFilters
        mode={mode}
        filters={filters}
        onChange={handleChangeFilters}
        className="mb-3"
        showModeFilter={!!clientId}
      />
      {isLoading && (
        <div className="flex h-full w-full items-center justify-center">
          <Spinner size="lg" variant="primary" />
        </div>
      )}
      <EmptyListState
        title={mapTabToEmptyStateText[mode].title}
        description={mapTabToEmptyStateText[mode].content}
        isLoading={isLoading}
        isSearchUsed={!isEmpty(filters)}
        itemsCount={tasks.length}
      />
      {!isLoading && !!tasks.length && (
        <div className="space-y-3">
          <InfiniteScroll
            next={fetchNextPage}
            dataLength={tasks.length}
            hasMore={!!hasNextPage}
            className="w-full flex flex-col space-y-2"
            style={{ overflow: 'unset' }}
            loader={
              <div className="flex justify-center items-center h-16 w-full">
                <Spinner size="md" variant="primary" />
              </div>
            }
            scrollableTarget="scrollable"
          >
            {tasks.map(task => {
              const props = {
                task,
                archiveEnabled: filters.archive,
                isUpcoming: [TaskListMode.Upcoming, TaskListMode.AllTasks].includes(mode),
                onOpenFromUrl: () => {
                  setTaskIdToOpenIdFromUrl(null);
                  setTaskResultIdToOpenFromUrl(null);
                },
                taskIdToOpen: taskIdToOpenIdFromUrl,
                taskResultIdToOpen: taskResultIdToOpenFromUrl,
                ...(mode === TaskListMode.History && { filterByName: filters.name }),
                clientId,
              };

              return (
                <div key={task.id} ref={el => (itemRefs.current[task.id] = el)}>
                  <TaskItem {...props} />
                </div>
              );
            })}
          </InfiniteScroll>
        </div>
      )}
    </PageLayout>
  );
};
export default withPrivateAccess(Tasks, Role.COMPLIANCE_ADMIN);
